import { useContext, useState, useEffect } from 'react';
import { Row, Col, Modal, Form, Input, Select, Button, message, Upload } from "antd-v4";
import { FirebaseContext } from '../../../../context/firebaseContext';
import { BackendContext } from '../../../../context/backendContext';
import { FeedbackType, getFeedbackAsString } from '@leedpulse-monorepo/enums';
import { useMobile } from '../../../../hooks/useMobile';
import { ISubscription } from '@leedpulse-monorepo/interfaces';


interface FeedbackModalProps {
    title: string;
    visible: boolean;
    conversationMessage: string;
    contactId: string;
    onCancel: () => void;
    onOk: () => void;
}

export default function FeedbackModal({
    title,
    visible,
    conversationMessage,
    contactId,
    onCancel,
    onOk
}: FeedbackModalProps) {
    const { userData, subscriptionId, getSubscriptionById } = useContext(FirebaseContext);
    const { postRequest } = useContext(BackendContext);

    const [feedbackType, setFeedbackType] = useState(() => conversationMessage ? FeedbackType.airelated : null);
    const [messageFeedback, setMessageFeedback] = useState<string | null>(null);
    const [fileImage, setFileImage] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [subscription, setSubscription] = useState<ISubscription | undefined>(undefined);
    const isMobile = useMobile(768);

    const loadSubscription = async () => {
        if (!subscriptionId) return;

        const resSub = await getSubscriptionById(subscriptionId);
        if (resSub.status === "success" && resSub.doc) {
            return setSubscription(resSub.doc);
        }
    };

    useEffect(() => {
        if (userData) {
            // Synchro avec un contexte qui fetche : l'etat local suit le resultat d'un
            // chargement declenche ici. Architecture Context + use case du repo.
            // eslint-disable-next-line react/set-state-in-effect
            loadSubscription();
        }
    }, [userData]);

    const handleSubmit = () => {
        if (!userData || !subscription || !feedbackType || !messageFeedback) return;
        setLoading(true);
        const formData = new FormData();
        formData.append('message', messageFeedback);
        formData.append('username', `${userData.firstname} ${userData.lastname}`);
        formData.append('email', userData.email);
        formData.append('subscriptionName', subscription.name);
        formData.append('category', feedbackType);
        if (conversationMessage) {
            formData.append('conversationMessage', conversationMessage);
        }
        if (contactId) {
            formData.append('contactId', contactId);
        }
        if (fileImage) {
            formData.append('image', fileImage);
        }

        postRequest('/api/v1/slack/feedback', formData, subscription.api_key).then((res) => {
            if (res.success) {
                message.success(res?.data?.message || 'Feedback envoyé avec succès');
                onOk();
            } else {
                message.error(res?.data?.message || 'Une erreur est survenue lors de l\'envoi du Feedback');
                throw new Error("Réponse invalide de lors de l'envoi de l'email");
            }
        }).catch((err) => {
            message.error(err.response?.data?.data?.message || 'Une erreur est survenue lors de l\'envoi du Feedback');
            throw new Error("Réponse invalide de lors de l'envoi de l'email");
        }).finally(() => {
            setLoading(false);
        });
    };

    return (
        <Modal
            className="feedback-modal"
            title={title}
            open={visible}
            footer={[
                <div key="footer-desktop">
                    <Button onClick={onCancel} style={{ marginRight: '8px' }}>
                        Annuler
                    </Button>
                    <Button 
                        loading={loading} 
                        type="primary" 
                        disabled={!messageFeedback || !feedbackType} 
                        onClick={handleSubmit}
                    >
                        Envoyer
                    </Button>
                </div>
            ]}
            onCancel={onCancel}
            destroyOnClose
            onOk={handleSubmit}
            closable={true}
            width={isMobile ? '90%' : '50%'}
        >
            <Form key={'stepper-create-contact'} layout="vertical">
                <Row gutter={[16, 4]}>
                    {userData?.firstname?.length > 0 && (
                        <Col md={userData?.lastname?.length > 0 ? 8 : 12} span={24}>
                            <Form.Item
                                className="hp-pl-sm-0"
                                label={userData?.lastname?.length > 0 ? "Prénom" : "Nom complet"}
                                name="firstname"
                            >
                                <Input
                                    defaultValue={userData?.firstname}
                                    disabled
                                />
                            </Form.Item>
                        </Col>
                    )}
                    {userData?.lastname?.length > 0 && (
                        <Col md={8} span={24}>
                            <Form.Item
                                className="hp-pl-sm-0"
                                label="Nom"
                                name="lastname"
                            >
                                <Input
                                    defaultValue={userData?.lastname}
                                    disabled
                                />
                            </Form.Item>
                        </Col>
                    )}
                    <Col md={userData?.lastname?.length > 0 ? 8 : 12} span={24}>
                        <Form.Item
                            label="Email"
                            name="email"
                        >
                            <Input
                                type="email"
                                defaultValue={userData?.email}
                                disabled
                            />
                        </Form.Item>
                    </Col>

                    <Col md={24} span={24}>
                        <Form.Item
                            className="hp-pl-sm-0"
                            label="Catégorie"
                            name="category"
                            rules={[
                                {
                                    required: true,
                                    message:
                                        "Veuillez entrer le type de feedback",
                                },
                            ]}
                        >
                            <Select
                                value={feedbackType}
                                defaultValue={conversationMessage ? 'airelated' : null}
                                placeholder='Sélectionnez votre feedback...'
                                disabled={loading}
                                onChange={(value) => setFeedbackType(value as FeedbackType)}
                            >
                                <Select.Option key={FeedbackType.utilization} value={FeedbackType.utilization}>
                                    {getFeedbackAsString(FeedbackType.utilization)}
                                </Select.Option>
                                <Select.Option key={FeedbackType.bug} value={FeedbackType.bug}>
                                    {getFeedbackAsString(FeedbackType.bug)}
                                </Select.Option>
                                <Select.Option key={FeedbackType.airelated} value={FeedbackType.airelated}>
                                    {getFeedbackAsString(FeedbackType.airelated)}
                                </Select.Option>
                                <Select.Option key={FeedbackType.subscriptionrequest} value={FeedbackType.subscriptionrequest}>
                                    {getFeedbackAsString(FeedbackType.subscriptionrequest)}
                                </Select.Option>
                                <Select.Option key={FeedbackType.suggestions} value={FeedbackType.suggestions}>
                                    {getFeedbackAsString(FeedbackType.suggestions)}
                                </Select.Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            name="contact-details"
                            label="Message"
                            rules={[{ required: true }]}
                        >
                            <Input.TextArea
                                value={messageFeedback || ''}
                                disabled={loading}
                                onChange={(event) => {
                                    setMessageFeedback(event.target.value);
                                }}
                                autoSize={{ maxRows: 6, minRows: 6 }}
                                placeholder={"Décrivez votre retour..."}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Upload
                            name='file'
                            accept='image/*'
                            disabled={loading}
                            multiple={false}
                            maxCount={1}
                            onChange={(info) => {
                                if (info.file.status === 'removed') {
                                    setFileImage(null);
                                }
                            }}
                            beforeUpload={(file) => {
                                setFileImage(file);
                                return false;
                            }}>
                            <Button type='ghost'>Ajouter une image...</Button>
                        </Upload>
                    </Col>
                </Row>
            </Form>
        </Modal >
    );
}
